import type { PriceErrorCode, SplitErrorCode } from '@wodi/domain';
import { PRICE_ERRORS, SPLIT_ERRORS, SPLIT_METHODS } from '@wodi/domain';
import { z } from 'zod';

import { ACTIVE_STATUSES } from './catalogue.ts';
import { actorSchema, isoDaySchema, reasonSchema, RECORD_STATUSES } from './clinical-common.ts';
import { amountSchema, signedAmountSchema } from './money.ts';

// Price lists, prices, which list applies to which payer, and the read-only quote used
// by pickers and billing previews (docs/plans/phase1-finance-catalogue.md §2.5–2.6, §2.9, §3.3).

export const priceListSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  currency: z.literal('KES'),
  basePriceListId: z.uuid().nullable(),
  /** Where the prices came from, for lists copied from a published tariff. */
  sourceReference: z.string().nullable(),
  status: z.enum(ACTIVE_STATUSES),
  statusReason: z.string().nullable(),
  version: z.number().int(),
});

export type PriceListView = z.infer<typeof priceListSchema>;

export const createPriceListRequestSchema = z.object({
  name: z.string().trim().min(2).max(120),
  basePriceListId: z.uuid().nullable().default(null),
  sourceReference: z.string().trim().min(1).max(500).nullable().default(null),
});

export const updatePriceListRequestSchema = z
  .object({
    expectedVersion: z.number().int().min(1),
    name: z.string().trim().min(2).max(120).optional(),
    basePriceListId: z.uuid().nullable().optional(),
    sourceReference: z.string().trim().min(1).max(500).nullable().optional(),
    status: z.enum(ACTIVE_STATUSES).optional(),
    statusReason: reasonSchema.optional(),
  })
  .refine((v) => v.status !== 'inactive' || v.statusReason !== undefined, {
    message: 'Say why the list is no longer used',
    path: ['statusReason'],
  });

export const priceSchema = z.object({
  id: z.uuid(),
  priceListId: z.uuid(),
  serviceItemId: z.uuid(),
  unitPrice: amountSchema,
  effectiveFrom: z.iso.datetime(),
  effectiveTo: z.iso.datetime().nullable(),
  status: z.enum(RECORD_STATUSES),
  statusReason: z.string().nullable(),
  supersededByPriceId: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  createdBy: actorSchema,
});

export type PriceView = z.infer<typeof priceSchema>;

export const priceListPricesQuerySchema = z.object({
  serviceItemId: z.uuid().optional(),
  /** Prices in effect at this instant. Default: now. Ignored with `includeHistory`. */
  at: z.iso.datetime().optional(),
  includeHistory: z
    .enum(['true', 'false'])
    .default('false')
    .transform((v) => v === 'true'),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.uuid().optional(),
});

/**
 * "Change price". Starts now or at 00:00 EAT on a future day (decision 1). The expected
 * current price guards against two people changing the same price at once.
 */
export const changePriceRequestSchema = z.object({
  serviceItemId: z.uuid(),
  unitPrice: amountSchema,
  startsOn: z.union([z.literal('now'), isoDaySchema]),
  /** The price the person was looking at; null when there was none. */
  expectedCurrentPriceId: z.uuid().nullable(),
});

export const priceListAssignmentSchema = z.object({
  id: z.uuid(),
  priceListId: z.uuid(),
  payerId: z.uuid(),
  schemeId: z.uuid().nullable(),
  facilityId: z.uuid().nullable(),
  status: z.enum(ACTIVE_STATUSES),
});

/** Set or clear the list for one (payer, scheme, facility) combination. */
export const putPriceListAssignmentRequestSchema = z.object({
  payerId: z.uuid(),
  schemeId: z.uuid().nullable(),
  facilityId: z.uuid().nullable(),
  /** Null removes the assignment; resolution then uses the next less specific one. */
  priceListId: z.uuid().nullable(),
});

// --- Quote ---------------------------------------------------------------------------

export const quoteRequestSchema = z.object({
  facilityId: z.uuid(),
  payerId: z.uuid(),
  schemeId: z.uuid().nullable().default(null),
  /** The clinical event time to price at (decision 2). Default: now. */
  at: z.iso.datetime().optional(),
  lines: z
    .array(
      z.object({
        serviceItemId: z.uuid(),
        quantity: z.number().int().min(1).max(100_000),
      }),
    )
    .min(1)
    .max(100),
});

export const PRICE_FAILURE_CODES = Object.keys(PRICE_ERRORS) as [
  PriceErrorCode,
  ...PriceErrorCode[],
];
export const SPLIT_FAILURE_CODES = Object.keys(SPLIT_ERRORS) as [
  SplitErrorCode,
  ...SplitErrorCode[],
];

const pricedPartSchema = z.object({
  serviceItemId: z.uuid(),
  quantity: z.number().int(),
  priceId: z.uuid(),
  priceListId: z.uuid(),
  assignedPriceListId: z.uuid(),
  /** Lists passed through before the price was found ("Cash price, via Staff list"). */
  fellBackThrough: z.array(z.uuid()),
  unitPrice: amountSchema,
  total: signedAmountSchema,
});

/** One line of a quote: priced and split, priced but no rule for who pays, or not priced. */
export const quoteLineSchema = z.discriminatedUnion('state', [
  pricedPartSchema.extend({
    state: z.literal('priced'),
    splitRuleId: z.uuid(),
    method: z.enum(SPLIT_METHODS),
    payerAmount: signedAmountSchema,
    patientAmount: signedAmountSchema,
  }),
  pricedPartSchema.extend({
    state: z.literal('no_split'),
    reason: z.enum(SPLIT_FAILURE_CODES),
    message: z.string(),
  }),
  z.object({
    state: z.literal('not_priced'),
    serviceItemId: z.uuid(),
    quantity: z.number().int(),
    reason: z.enum(PRICE_FAILURE_CODES),
    message: z.string(),
  }),
]);

export type QuoteLineView = z.infer<typeof quoteLineSchema>;

export const quoteResponseSchema = z.object({
  at: z.iso.datetime(),
  lines: z.array(quoteLineSchema),
});

export const enteredInErrorPriceRequestSchema = z.object({ reason: reasonSchema });
